import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { fetchOrganizationMembers, sendInviteLink, removeUserFromOrganization } from '../utils/organizationUtils'; // Import utility function
import { fetchUserInfo } from '../utils/userInfoUtils';
import '../css/OrganizationModal.css'; // Import CSS for modal styling

function OrganizationModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]); // Initialize with an empty array
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [showAddMember, setShowAddMember] = useState(false); // Show Add Member form
  const [newMemberEmail, setNewMemberEmail] = useState(''); // State for new member email
  const [emailError, setEmailError] = useState(null); // Email validation error state
  const [currentUserEmail, setCurrentUserEmail] = useState('');

  useEffect(() => {
    if (isOpen) {
      const loadOrganizationMembers = async () => {
        setLoading(true); // Set loading to true when the modal opens
        try {
          const userInfo = await fetchUserInfo();
          const membersList = await fetchOrganizationMembers();
          setCurrentUserEmail(userInfo.email || '');
          setMembers(Array.isArray(membersList) ? membersList : []); // Ensure it's an array
          setError(null); // Clear any previous errors
        } catch (error) {
          setError(t('organization.loadError'));
          setMembers([]); // Set members to an empty array in case of error
        } finally {
          setLoading(false); // Stop loading once fetch is complete
        }
      };

      loadOrganizationMembers();
    }
  }, [isOpen, t]);

  const handleRemove = async (email) => {
    try {
      // Call the function that sends the request to remove the user
      await removeUserFromOrganization(email);

      // Update the UI
      setMembers(members.filter((member) => member !== email));
      console.log(`Successfully removed ${email}`);

      // Could also add a success message/toast here if desired

    } catch (error) {
      // Keep the UI state as is and show error
      alert(t('errors.organization'));
      console.error('Error removing user:', error.message);
    }
  };

  const handleAddMemberClick = () => {
    setShowAddMember(true); // Show the Add Member form
  };

  const handleEmailInputChange = (e) => {
    setNewMemberEmail(e.target.value);
    setEmailError(null); // Clear any previous error on new input
  };

  const handleSendInvite = async () => {
    try {
      // Call the utility function to handle everything
      const message = await sendInviteLink(newMemberEmail);

      console.log('Invite sent:', message);

      // Reset the form after success
      setNewMemberEmail('');
      setShowAddMember(false);
    } catch (error) {
      // Handle any errors that occur in the utility function
      setEmailError(t('errors.invite'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{t('account.organization')}</h2>

        {error && <p className="error-message">{error}</p>}

        <table className="members-table">
          <thead>
            <tr>
              <th>{t('common.email')}</th>
              <th>{t('organization.action')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="2">{t('organization.loading')}</td>
              </tr>
            ) : (
              members.length > 0 ? (
                members.map((email) => (
                  <tr key={email}>
                    <td>{email}</td>
                    <td>
                      <button
                        className="remove-button"
                        onClick={() => handleRemove(email)}
                      >
                        {email === currentUserEmail ? t('common.leave') : t('common.remove')}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2">{t('organization.empty')}</td>
                </tr>
              )
            )}
          </tbody>
        </table>

        {/* Add member form */}
        {showAddMember ? (
          <div className="add-member-form">
            <input
              type="text"
              value={newMemberEmail}
              onChange={handleEmailInputChange}
              placeholder={t('organization.emailPlaceholder')}
              className="email-input"
            />
            <button className="send-invite-button" onClick={handleSendInvite}>{t('organization.sendInvite')}</button>
            {emailError && <p className="error-message">{emailError}</p>}
          </div>
        ) : (
          <button className="add-member-button" onClick={handleAddMemberClick}>{t('organization.add')}</button>
        )}

        {/* Close modal button */}
        <button className="close-button-org-modal" onClick={onClose}>{t('common.close')}</button>
      </div>
    </div>
  );
}

export default OrganizationModal;
